import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  PackageSearch, FlaskConical, Box, FileCheck, MoreHorizontal, Plus, Search, 
  Download, Edit2, Trash2, FileText, ArrowUpDown, DollarSign, Landmark, ChevronDown, ChevronUp
} from 'lucide-react';
import { exportToPDF } from '@/utils/exportPDF';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useProductionExpenses } from '@/hooks/useProductionExpenses';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import AsaMutasiConfirm from '@/components/finance/AsaMutasiConfirm';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const MAIN_CATEGORIES = [
  { key: 'Packaging', label: 'Packaging', icon: PackageSearch, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { key: 'Bahan Cair', label: 'Bahan Cair', icon: FlaskConical, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'Item Tambahan', label: 'Item Tambahan', icon: Box, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'Legalitas', label: 'Legalitas', icon: FileCheck, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'Lainnya', label: 'Lainnya', icon: MoreHorizontal, color: 'bg-slate-50 text-slate-700 border-slate-200' }
];

const SUB_CATEGORIES = {
  'Packaging': ['Fullset', 'Botol', 'Tutup', 'Spray', 'Lainnya'],
  'Bahan Cair': ['Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'Peg', 'Sustain', 'Quidex', 'Castor Oil', 'Lainnya'],
  'Item Tambahan': ['Stiker', 'Box', 'Sablon', 'Hot Print', 'Gift Card', 'Coloring', 'Lainnya'],
  'Legalitas': ['BPOM', 'HAKI', 'Halal', 'Lainnya'],
  'Lainnya': ['Operasional Produksi', 'Jasa', 'Lainnya']
};

export default function FinanceProductionCosts() {
  const queryClient = useQueryClient();
  
  const { accountsMap: accounts = {} } = useBankAccounts();

  const { data: coaList = [] } = useQuery({ 
    queryKey: ['coa'], 
    queryFn: () => base44.entities.ChartOfAccounts.list() 
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.AccountMutation.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
    }
  });

  const { expenses = [], isLoading: loadingExpenses, createExpense: addExpense, updateExpense, deleteExpense } = useProductionExpenses();

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount,
    totalCount
  } = useBatchSelection(expenses, e => e.id);

  // === FETCH INVENTORY DATA FOR ITEM PICKER ===
  const { data: bahanCairList = [] } = useQuery({ queryKey: ['bahan-cair-prodcost'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: botolList = [] } = useQuery({ queryKey: ['botol-prodcost'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup-prodcost'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray-prodcost'], queryFn: () => base44.entities.Spray.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan-prodcost'], queryFn: () => base44.entities.ItemTambahan.list() });

  // Map inventory type → production expense main category + sub category
  const TYPE_TO_CATEGORY = {
    'Botol': { category: 'Packaging', subCategory: 'Botol' },
    'Tutup': { category: 'Packaging', subCategory: 'Tutup' },
    'Spray': { category: 'Packaging', subCategory: 'Spray' },
    'Bahan Cair': { category: 'Bahan Cair', subCategory: 'Lainnya' },
    'Item Tambahan': { category: 'Item Tambahan', subCategory: 'Lainnya' },
  };

  const allInventoryItems = useMemo(() => [
    ...bahanCairList.map(i => ({ ...i, _type: 'Bahan Cair', _sub: i.kategori || 'Lainnya', _label: i.nama || i.item_name || '—' })),
    ...botolList.map(i => ({ ...i, _type: 'Botol', _sub: 'Botol', _label: i.nama || i.item_name || '—' })),
    ...tutupList.map(i => ({ ...i, _type: 'Tutup', _sub: 'Tutup', _label: i.nama || i.item_name || '—' })),
    ...sprayList.map(i => ({ ...i, _type: 'Spray', _sub: 'Spray', _label: i.nama || i.item_name || '—' })),
    ...itemTambahanList.map(i => ({ ...i, _type: 'Item Tambahan', _sub: i.kategori || 'Lainnya', _label: i.nama || i.item_name || '—' })),
  ].sort((a, b) => (a._label || '').localeCompare(b._label || '')), [bahanCairList, botolList, tutupList, sprayList, itemTambahanList]);

  const [inventorySearch, setInventorySearch] = useState('');

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        const expToDelete = expenses.find(e => e.id === id);
        if (!expToDelete) continue;
        await deleteExpense(id);
        // Tidak lagi mengubah Mutasi Rekening saat dihapus
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      toast.success(`${selectedCount} biaya produksi berhasil dihapus.`);
      clearSelection();
    }
  });

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // CRUD Form States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [asaConfirmData, setAsaConfirmData] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [expandedItemIndex, setExpandedItemIndex] = useState(0);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    bankAccountId: 'mandiri',
    metodePembayaran: 'Cash',
    tanggalBarangDatang: '',
    tanggalJatuhTempo: '',
    coa_allocations: [],
    items: [
      {
        category: 'Packaging',
        subCategory: 'Botol',
        itemName: '',
        qty: 1,
        hargaBeli: '',
        amountOverride: ''
      }
    ]
  });

  // Handle Main Category change
  const handleCategoryChange = (cat, index) => {
    const newItems = [...form.items];
    newItems[index] = {
      ...newItems[index],
      category: cat,
      subCategory: SUB_CATEGORIES[cat]?.[0] || 'Lainnya'
    };
    setForm({ ...form, items: newItems });
  };

  // Filter Logic
  const filteredExpenses = useMemo(() => {
    return expenses.filter(exp => {
      const desc = String(exp.description || '');
      const cat = String(exp.category || '');
      const sub = String(exp.subCategory || '');
      const date = exp.date || '';
      const matchSearch = !search || 
        desc.toLowerCase().includes(search.toLowerCase()) ||
        cat.toLowerCase().includes(search.toLowerCase()) ||
        sub.toLowerCase().includes(search.toLowerCase());
      const matchCat = categoryFilter === 'all' || cat === categoryFilter;
      const matchFrom = !dateFrom || date >= dateFrom;
      const matchTo = !dateTo || date <= dateTo;
      return matchSearch && matchCat && matchFrom && matchTo;
    }).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [expenses, search, categoryFilter, dateFrom, dateTo]);

  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage) || 1;
  const paginatedExpenses = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredExpenses.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredExpenses, currentPage, itemsPerPage]);

  // Filter pickable inventory items based on current category and subCategory
  const getFilteredPickableItems = (category, subCategory) => {
    return allInventoryItems.filter(item => {
       let itemMainCategory = '';
       if (['Botol', 'Tutup', 'Spray'].includes(item._type)) itemMainCategory = 'Packaging';
       else if (item._type === 'Bahan Cair') itemMainCategory = 'Bahan Cair';
       else if (item._type === 'Item Tambahan') itemMainCategory = 'Item Tambahan';
       
       if (itemMainCategory !== category) return false;

       if (category === 'Packaging') {
          if (subCategory === 'Botol') return item._type === 'Botol';
          if (subCategory === 'Tutup') return item._type === 'Tutup';
          if (subCategory === 'Spray') return item._type === 'Spray';
          if (subCategory === 'Fullset' || subCategory === 'Lainnya') return true; 
       } else {
          if (item._sub === subCategory) return true;
          if (subCategory === 'Lainnya' && !['Stiker', 'Box', 'Gift Card', 'Sablon', 'Hot Print', 'Coloring', 'Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'Peg', 'Sustain'].includes(item._sub)) return true;
       }
       return false;
    });
  };

  const totalAmount = useMemo(() => {
    return filteredExpenses.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [filteredExpenses]);

  const categoryBreakdown = useMemo(() => {
    const breakdown = {};
    MAIN_CATEGORIES.forEach(c => { breakdown[c.key] = 0; });
    filteredExpenses.forEach(exp => {
      const cat = exp.category || 'Packaging';
      breakdown[cat] = (breakdown[cat] || 0) + (Number(exp.amount) || 0);
    });
    return breakdown;
  }, [filteredExpenses]);

  const largestCategory = useMemo(() => {
    let maxCat = '—';
    let maxVal = 0;
    Object.entries(categoryBreakdown).forEach(([cat, val]) => {
      if (val > maxVal) {
        maxVal = val;
        maxCat = cat;
      }
    });
    return { name: maxCat, value: maxVal };
  }, [categoryBreakdown]);

  // Actions
  const handleOpenAdd = () => {
    setEditItem(null);
    setExpandedItemIndex(0);
    setForm({
      date: new Date().toISOString().split('T')[0],
      bankAccountId: 'mandiri',
      metodePembayaran: 'Cash',
      tanggalBarangDatang: '',
      tanggalJatuhTempo: '',
      coa_allocations: [],
      items: [
        {
          category: 'Packaging',
          subCategory: 'Botol',
          itemName: '',
          qty: 1,
          hargaBeli: '',
          amountOverride: ''
        }
      ]
    });
    setShowFormModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditItem(item);
    setExpandedItemIndex(0);
    const itemQty = Number(item.qty) || 1;
    // Resolve hargaBeli: use stored value if > 0, otherwise compute from amount/qty
    let resolvedHargaBeli = Number(item.hargaBeli) || 0;
    if (resolvedHargaBeli === 0 && item.amount) {
      resolvedHargaBeli = Math.round(Number(item.amount) / itemQty);
    }
    setForm({
      date: item.date,
      bankAccountId: item.bankAccountId || 'mandiri',
      metodePembayaran: item.metodePembayaran || 'Cash',
      tanggalBarangDatang: item.tanggalBarangDatang || '',
      tanggalJatuhTempo: item.tanggalJatuhTempo || '',
      coa_allocations: item.coa_allocations || [],
      items: [
        {
          category: item.category || 'Packaging',
          subCategory: item.subCategory || 'Botol',
          itemName: item.itemName || item.description || '',
          qty: itemQty,
          hargaBeli: resolvedHargaBeli.toString(),
          amountOverride: item.amount ? item.amount.toString() : ''
        }
      ]
    });
    setShowFormModal(true);
  };

  const handleDelete = async (id) => {
    if (confirm('Apakah Anda yakin ingin menghapus biaya produksi ini?')) {
      const expToDelete = expenses.find(e => e.id === id);
      await deleteExpense(id);
      
      // Tidak lagi mengubah Mutasi Rekening saat dihapus
      
      toast.success('Biaya produksi berhasil dihapus.');
    }
  };

  const processSaveEdit = async () => {
    try {
        const refId = editItem.batch_id || `prodcost-${editItem.id}`;
        
        for (let i = 0; i < form.items.length; i++) {
          const itm = form.items[i];
          const hBeli = Number(itm.hargaBeli) || 0;
          const quantity = Number(itm.qty) || 1;
          const totalAmount = itm.amountOverride !== undefined && itm.amountOverride !== '' 
                                ? Number(itm.amountOverride) 
                                : hBeli * quantity;

          const payload = {
            date: form.date,
            category: itm.category,
            subCategory: itm.subCategory,
            bankAccountId: form.bankAccountId || 'mandiri',
            itemName: itm.itemName,
            qty: quantity,
            hargaBeli: hBeli,
            amount: totalAmount,
            description: itm.itemName,
            metodePembayaran: form.metodePembayaran,
            tanggalBarangDatang: form.metodePembayaran === 'Tempo' ? form.tanggalBarangDatang : null,
            tanggalJatuhTempo: form.metodePembayaran === 'Tempo' ? form.tanggalJatuhTempo : null,
            batch_id: refId
          };

          if (i === 0) {
            // Update the existing item
            await updateExpense(editItem.id, payload);
          } else {
            // Add as new item
            await addExpense(payload);
          }
        }
        queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
        toast.success(`Berhasil menyimpan perubahan dan item baru.`);
        setShowFormModal(false);
        setAsaConfirmData(null);
    } catch (err) {
      console.error(err);
      toast.error('Gagal menyimpan biaya produksi.');
      if (asaConfirmData) setAsaConfirmData(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleAsaConfirm = async (shouldUpdate) => {
    setAsaConfirmData(prev => ({ ...prev, isProcessing: true }));
    if (shouldUpdate && asaConfirmData) {
      try {
        const existingMutations = await base44.entities.AccountMutation.filter({ reference_id: asaConfirmData.refId });
        if (existingMutations && existingMutations.length > 0) {
           const mut = existingMutations[0];
           await base44.entities.AccountMutation.update(mut.id, { 
              amount: mut.amount + asaConfirmData.diff 
           });
        }
      } catch (err) {
        console.error("Gagal update mutasi:", err);
      }
    }
    await processSaveEdit();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all items
    for (const item of form.items) {
      if (!item.itemName || !item.hargaBeli || Number(item.qty) <= 0) {
        toast.error('Mohon lengkapi semua field item dengan benar.');
        return;
      }
    }

    try {
      if (editItem) {
        // Edit Mode: Check for difference to trigger ASA Confirm
        const itm = form.items[0];
        const hBeli = Number(itm.hargaBeli) || 0;
        const quantity = Number(itm.qty) || 1;
        const totalAmount = itm.amountOverride !== undefined && itm.amountOverride !== '' 
                              ? Number(itm.amountOverride) 
                              : hBeli * quantity;
        const diff = totalAmount - (editItem.amount || 0);
        const refId = editItem.batch_id || `prodcost-${editItem.id}`;
        
        if (diff !== 0) {
           setAsaConfirmData({
             oldAmount: editItem.amount || 0,
             newAmount: totalAmount,
             date: form.date,
             diff,
             refId,
             isProcessing: false
           });
           return; // Pause execution, wait for modal
        }
        
        await processSaveEdit();
      } else {
        // Add Mode: Multiple items grouped into 1 mutation
        const bulkId = `bulkprod-${Date.now()}`;
        let totalBulkAmount = 0;
        
        for (const itm of form.items) {
          const hBeli = Number(itm.hargaBeli) || 0;
          const quantity = Number(itm.qty) || 1;
          const totalAmount = itm.amountOverride !== undefined && itm.amountOverride !== '' 
                                ? Number(itm.amountOverride) 
                                : hBeli * quantity;

          const payload = {
            date: form.date,
            category: itm.category,
            subCategory: itm.subCategory,
            bankAccountId: form.bankAccountId || 'mandiri',
            itemName: itm.itemName,
            qty: quantity,
            hargaBeli: hBeli,
            amount: totalAmount,
            description: itm.itemName,
            metodePembayaran: form.metodePembayaran,
            tanggalBarangDatang: form.metodePembayaran === 'Tempo' ? form.tanggalBarangDatang : null,
            tanggalJatuhTempo: form.metodePembayaran === 'Tempo' ? form.tanggalJatuhTempo : null,
            batch_id: bulkId
          };

          totalBulkAmount += totalAmount;
          await addExpense(payload);
        }
        
        // Auto create 1 single mutation for all items
        const accountName = accounts[form.bankAccountId]?.name || 'Mandiri Satala';
        let mutasiKategori = form.items[0].category === 'Legalitas' ? 'Legalitas' : 'Bahan Baku';
        let mutasiDesc = form.items.length > 1 
            ? `[${mutasiKategori}] Biaya Produksi: Pembelian ${form.items.length} Item` 
            : `[${mutasiKategori}] Biaya Produksi: ${form.items[0].category} - ${form.items[0].subCategory} (${form.items[0].itemName})`;

        await createMutation.mutateAsync({
          date: form.date,
          type: 'keluar',
          account: accountName,
          amount: totalBulkAmount,
          description: mutasiDesc,
          reference_id: bulkId,
          coa_allocations: form.coa_allocations
        });
        
        toast.success(`${form.items.length} biaya produksi baru berhasil ditambahkan.`);
      }
      setShowFormModal(false);
    } catch (err) {
      console.error(err);
      toast.error('Gagal menyimpan biaya produksi.');
    }
  };

  const handleExportPDF = () => {
    exportToPDF({
      title: 'Laporan Biaya Produksi Satala Dermatech',
      subtitle: `Periode: ${dateFrom || 'Semua'} s/d ${dateTo || 'Semua'} | Total Pengeluaran: Rp ${fmt(totalAmount)}`,
      filename: `Biaya-Produksi-${new Date().toISOString().split('T')[0]}`,
      columns: [
        { key: 'date', label: 'Tanggal', render: r => r.date },
        { key: 'category', label: 'Kategori', render: r => `${r.category} (${r.subCategory})` },
        { key: 'description', label: 'Keterangan', render: r => r.description },
        { key: 'amount', label: 'Jumlah (Rp)', render: r => `Rp ${fmt(r.amount)}` }
      ],
      rows: filteredExpenses
    });
  };

  return (
    <div className="space-y-8 pb-16 text-[#302621]">
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={filteredExpenses.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={() => {
          if (confirm(`Yakin ingin menghapus ${selectedCount} biaya produksi terpilih?`)) {
            batchDeleteMutation.mutate(Array.from(selectedIds));
          }
        }}
        isDeleting={batchDeleteMutation.isPending}
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-black tracking-tight text-slate-800">Biaya Produksi</h1>
          <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1">
            Catat dan pantau pengeluaran biaya produksi seperti packaging, bahan cair, item tambahan, dan legalitas.
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleExportPDF}
            variant="outline" 
            className="gap-2 text-xs font-bold border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] rounded-xl h-10 shadow-xs"
          >
            <Download className="w-3.5 h-3.5" /> Ekspor PDF
          </Button>
          <Button 
            onClick={handleOpenAdd}
            className="bg-[#8C6208] text-white hover:bg-[#724F06] gap-2 text-xs font-bold rounded-xl h-10 shadow-sm"
          >
            <Plus className="w-4 h-4" /> Tambah Biaya
          </Button>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Expense Card */}
        <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <DollarSign className="w-24 h-24" />
          </div>
          <CardContent className="p-4 md:p-6">
            <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Biaya Produksi</p>
            <p className="text-2xl md:text-3xl font-black text-slate-800 mt-1.5">Rp {fmt(totalAmount)}</p>
            <p className="text-[8px] md:text-[10px] text-slate-400 mt-1">Total akumulasi dari filter saat ini</p>
          </CardContent>
        </Card>

        {/* Largest Category */}
        <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <ArrowUpDown className="w-24 h-24" />
          </div>
          <CardContent className="p-4 md:p-6">
            <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider">Kategori Terbesar</p>
            <p className="text-lg md:text-xl font-black text-slate-800 mt-2 truncate">{largestCategory.name}</p>
            <p className="text-[10px] md:text-xs text-rose-600 font-semibold mt-1">Rp {fmt(largestCategory.value)}</p>
          </CardContent>
        </Card>

        {/* Total Transactions */}
        <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <FileText className="w-24 h-24" />
          </div>
          <CardContent className="p-4 md:p-6">
            <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider">Jumlah Transaksi</p>
            <p className="text-2xl md:text-3xl font-black text-slate-800 mt-1.5">{filteredExpenses.length} <span className="text-[10px] md:text-xs text-slate-400 font-bold uppercase">Record</span></p>
            <p className="text-[8px] md:text-[10px] text-slate-400 mt-1">Transaksi biaya produksi terdaftar</p>
          </CardContent>
        </Card>
      </div>

      {/* Category Progress Bars */}
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl p-6">
        <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest mb-4">Proporsi Biaya Produksi per Kategori</h3>
        <div className="space-y-4">
          {[...MAIN_CATEGORIES]
            .sort((a, b) => (categoryBreakdown[b.key] || 0) - (categoryBreakdown[a.key] || 0))
            .map(cat => {
            const val = categoryBreakdown[cat.key] || 0;
            const pct = totalAmount > 0 ? (val / totalAmount) * 100 : 0;
            const Icon = cat.icon;
            return (
              <div key={cat.key} className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <div className="flex items-center gap-2">
                    <div className="p-1 rounded bg-slate-100 text-slate-600">
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="font-bold text-slate-700">{cat.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-800">Rp {fmt(val)}</span>
                    <span className="text-[10px] text-slate-400">({pct.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div 
                    className="bg-[#8C6208] h-full rounded-full transition-all duration-500" 
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Filter and CRUD List Table */}
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-slate-50/50">
          <div>
            <h3 className="text-base font-bold text-slate-800">Daftar Transaksi Produksi</h3>
            <p className="text-xs text-slate-500">Kelola dan filter data pengeluaran bahan dan produksi.</p>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 bg-slate-50/20 border-b border-slate-100 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">Kategori:</span>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-9 text-xs w-44 bg-white border-[#E6D5B8]/60">
                <SelectValue placeholder="Semua Kategori" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Kategori</SelectItem>
                {MAIN_CATEGORIES.map(c => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Cari deskripsi atau tipe..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">Dari:</span>
            <CustomDatePicker 
              value={dateFrom} 
              onChange={setDateFrom} 
              className="h-9 text-xs w-36" 
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">S/d:</span>
            <CustomDatePicker 
              value={dateTo} 
              onChange={setDateTo} 
              className="h-9 text-xs w-36" 
            />
          </div>

          {(dateFrom || dateTo || search || categoryFilter !== 'all') && (
            <Button 
              variant="ghost" 
              onClick={() => { setDateFrom(''); setDateTo(''); setSearch(''); setCategoryFilter('all'); setCurrentPage(1); }}
              className="text-xs h-9 text-slate-500 hover:text-slate-800"
            >
              Reset
            </Button>
          )}
        </div>

        {/* Table list */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200">
                {isSelectionMode && <th className="p-4 w-12"></th>}
                <th className="p-4">Tanggal</th>
                <th className="p-4">Kategori & Tipe</th>
                <th className="p-4">Item & Qty</th>
                <th className="p-4 text-right">Harga Satuan</th>
                <th className="p-4 text-right">Total Pengeluaran</th>
                {!isSelectionMode && <th className="p-4 text-center">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
              {paginatedExpenses.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-slate-400">Belum ada pengeluaran produksi terdaftar.</td>
                </tr>
              ) : (
                paginatedExpenses.map((exp) => {
                  const meta = MAIN_CATEGORIES.find(c => c.key === exp.category) || {};
                  return (
                    <tr 
                      key={exp.id} 
                      onClick={() => isSelectionMode && toggleSelection(exp.id)}
                      className={`transition-colors ${isSelectionMode ? 'cursor-pointer' : ''} ${
                        selectedIds.has(exp.id) ? 'bg-rose-50/60' : 'hover:bg-slate-50/50'
                      }`}
                    >
                      {isSelectionMode && (
                        <td className="p-4">
                          <Checkbox checked={selectedIds.has(exp.id)} onCheckedChange={() => toggleSelection(exp.id)} />
                        </td>
                      )}
                      <td className="p-4 font-mono text-slate-400">{exp.date}</td>
                      <td className="p-4">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${meta.color || 'bg-slate-50 text-slate-700'}`}>
                            {exp.category}
                          </span>
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{exp.subCategory}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="text-slate-700 text-xs font-semibold">{exp.itemName || exp.description}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-slate-500">{exp.qty || 1} unit</span>
                            {exp.metodePembayaran === 'Tempo' && (
                              <span className="inline-block px-1.5 py-0.5 rounded text-[8px] font-bold bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider">
                                Tempo: {exp.tanggalJatuhTempo || '?'}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex flex-col items-end">
                          <span className="text-[11px] font-medium text-slate-600">Rp {fmt(exp.hargaBeli ?? exp.amount)}</span>
                        </div>
                      </td>
                      <td className="p-4 text-right font-black text-sm text-slate-800">
                        Rp {fmt(exp.amount)}
                      </td>
                      {!isSelectionMode && (
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="w-7 h-7 hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEdit(exp);
                              }}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="w-7 h-7 hover:bg-rose-50 text-rose-500 hover:text-rose-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                startSelection(exp.id);
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination Controls ── */}
        {filteredExpenses.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-100 bg-white flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
              <div className="w-[80px]">
                <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                  <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="w-8 h-8 rounded-xl border-slate-200 text-slate-500"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </Button>
              <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-[#8C6208] text-white font-bold text-xs shadow-sm">
                {currentPage}
              </div>
              <Button
                variant="outline"
                size="icon"
                className="w-8 h-8 rounded-xl border-slate-200 text-slate-500"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* ADD/EDIT FORM DIALOG MODAL */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/40 -xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden border border-slate-100">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 text-[#302621]">
              <h3 className="font-bold text-base text-slate-800">
                {editItem ? 'Edit Biaya Produksi' : 'Tambah Biaya Produksi'}
              </h3>
              <button onClick={() => setShowFormModal(false)} className="text-slate-400 hover:text-slate-600 text-lg">×</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4 text-xs text-[#302621]">
                <div className="grid grid-cols-2 gap-4 border-b border-slate-100 pb-4">
                  <div className="space-y-1">
                    <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Tanggal Transaksi</Label>
                    <CustomDatePicker 
                      value={form.date} 
                      onChange={val => setForm({ ...form, date: val })}
                      className="h-9"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Sumber Rekening</Label>
                    <Select 
                      value={form.bankAccountId} 
                      onValueChange={val => setForm({ ...form, bankAccountId: val })}
                    >
                      <SelectTrigger className="h-9 bg-white border-blue-200 focus:ring-blue-500">
                        <div className="flex items-center gap-2">
                          <Landmark className="w-3.5 h-3.5 text-blue-500" />
                          <span>{accounts[form.bankAccountId]?.name || 'Pilih Rekening'}</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="bca">{accounts.bca?.name || 'BCA'}</SelectItem>
                        <SelectItem value="mandiri">{accounts.mandiri?.name || 'Mandiri'}</SelectItem>
                        <SelectItem value="bni">{accounts.bni?.name || 'BNI'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1 col-span-2 sm:col-span-1">
                    <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Metode Pembayaran</Label>
                    <Select 
                      value={form.metodePembayaran} 
                      onValueChange={val => setForm({ ...form, metodePembayaran: val })}
                    >
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue placeholder="Pilih Metode Pembayaran" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="Cash">Cash (Bayar Langsung)</SelectItem>
                        <SelectItem value="Tempo">Tempo (Bayar Nanti)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {form.metodePembayaran === 'Tempo' && (
                    <>
                      <div className="space-y-1">
                        <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Tgl Brg Datang</Label>
                        <CustomDatePicker
                          value={form.tanggalBarangDatang} 
                          onChange={val => setForm({ ...form, tanggalBarangDatang: val })}
                          className="h-9"
                          required={form.metodePembayaran === 'Tempo'}
                        />
                      </div>
                      <div className="space-y-1 col-span-2">
                        <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Tgl Jatuh Tempo</Label>
                        <CustomDatePicker
                          value={form.tanggalJatuhTempo} 
                          onChange={val => setForm({ ...form, tanggalJatuhTempo: val })}
                          className="h-9"
                          required={form.metodePembayaran === 'Tempo'}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-bold text-slate-700 text-sm">Daftar Item Produksi</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setForm({
                          ...form,
                          items: [...form.items, { category: 'Packaging', subCategory: 'Botol', itemName: '', qty: 1, hargaBeli: '' }]
                        });
                        setExpandedItemIndex(form.items.length);
                      }}
                      className="h-7 text-xs font-bold text-[#8C6208] hover:bg-[#F9F5EC]"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Tambah Item
                    </Button>
                  </div>

                  {form.items.map((item, index) => {
                    const isExpanded = expandedItemIndex === index;
                    const itemTotal = item.amountOverride !== undefined && item.amountOverride !== '' 
                                        ? Number(item.amountOverride) 
                                        : (Number(item.qty) || 0) * (Number(item.hargaBeli) || 0);

                    return (
                      <div key={index} className={`border rounded-xl overflow-hidden transition-all duration-300 ${isExpanded ? 'border-[#E6D5B8] bg-white shadow-sm' : 'border-slate-200 bg-slate-50/50 hover:bg-slate-50 cursor-pointer'}`}>
                        {/* Header Accordion */}
                        <div 
                          className="px-4 py-3 flex items-center justify-between"
                          onClick={() => setExpandedItemIndex(isExpanded ? null : index)}
                        >
                          <div className="flex items-center gap-3 truncate">
                            <div className="w-6 h-6 rounded-full bg-[#E6D5B8]/30 flex items-center justify-center text-[#8C6208] font-bold text-xs shrink-0">
                              {index + 1}
                            </div>
                            <div className="flex flex-col truncate">
                              <span className="font-bold text-slate-800 text-xs truncate">{item.itemName || `Item ${index + 1}`}</span>
                              {itemTotal > 0 && <span className="text-[10px] text-slate-500 font-semibold">Rp {fmt(itemTotal)}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {!editItem && form.items.length > 1 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
                                  if (expandedItemIndex === index) setExpandedItemIndex(null);
                                }}
                                className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-rose-500 bg-white rounded-md border border-slate-200 shadow-sm transition-colors"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                        </div>

                        {/* Content Accordion */}
                        {isExpanded && (
                          <div className="p-4 border-t border-slate-100 bg-white space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Kategori Utama</Label>
                                <Select 
                                  value={item.category} 
                                  onValueChange={val => handleCategoryChange(val, index)}
                                >
                                  <SelectTrigger className="h-9 bg-slate-50">
                                    <SelectValue placeholder="Pilih Kategori" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white">
                                    {MAIN_CATEGORIES.map(c => (
                                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1">
                                <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Sub Kategori</Label>
                                <Select 
                                  value={item.subCategory} 
                                  onValueChange={val => {
                                    const newItems = [...form.items];
                                    newItems[index].subCategory = val;
                                    setForm({ ...form, items: newItems });
                                  }}
                                >
                                  <SelectTrigger className="h-9 bg-slate-50">
                                    <SelectValue placeholder="Pilih Tipe" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white">
                                    {SUB_CATEGORIES[item.category]?.map(t => (
                                      <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="space-y-1">
                              <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Pilih dari Inventori (Opsional)</Label>
                              <Select onValueChange={(val) => {
                                if (!val) return;
                                const [type, id] = val.split('_id_');
                                const invItem = allInventoryItems.find(i => String(i.id) === id && i._type === type);
                                if (invItem) {
                                  const mapped = TYPE_TO_CATEGORY[invItem._type] || {};
                                  const newItems = [...form.items];
                                  newItems[index] = {
                                    ...newItems[index],
                                    itemName: invItem._label,
                                    category: mapped.category || newItems[index].category,
                                    subCategory: mapped.subCategory || newItems[index].subCategory,
                                    hargaBeli: String(invItem.harga_rupiah || invItem.harga_beli || '')
                                  };
                                  setForm({ ...form, items: newItems });
                                }
                              }}>
                                <SelectTrigger className="h-9 bg-white border-blue-200 focus:ring-blue-500">
                                  <SelectValue placeholder="-- Pilih item otomatis --" />
                                </SelectTrigger>
                                <SelectContent className="bg-white max-h-[250px]">
                                  <div className="p-2 sticky top-0 bg-white border-b border-slate-100">
                                    <Input
                                      placeholder="Cari item inventori..."
                                      value={inventorySearch}
                                      onChange={(e) => setInventorySearch(e.target.value)}
                                      className="h-8 text-xs"
                                      onClick={e => e.stopPropagation()}
                                      onKeyDown={e => e.stopPropagation()}
                                    />
                                  </div>
                                  {getFilteredPickableItems(item.category, item.subCategory)
                                    .filter(i => !inventorySearch || i._label.toLowerCase().includes(inventorySearch.toLowerCase()) || i._type.toLowerCase().includes(inventorySearch.toLowerCase()))
                                    .map(i => (
                                    <SelectItem key={`${i._type}_id_${i.id}`} value={`${i._type}_id_${i.id}`}>
                                      {i._label} ({i._type}){i.harga_rupiah ? ` — Rp ${fmt(i.harga_rupiah)}` : ''}
                                    </SelectItem>
                                  ))}
                                  {getFilteredPickableItems(item.category, item.subCategory).filter(i => !inventorySearch || i._label.toLowerCase().includes(inventorySearch.toLowerCase())).length === 0 && (
                                    <div className="text-center text-xs text-slate-400 py-3">
                                      Tidak ada item inventori
                                    </div>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-1">
                              <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Nama Item / Deskripsi <span className="text-rose-500">*</span></Label>
                              <Input 
                                placeholder="Contoh: Botol Laliq 30ml"
                                value={item.itemName} 
                                onChange={e => {
                                  const newItems = [...form.items];
                                  newItems[index].itemName = e.target.value;
                                  setForm({ ...form, items: newItems });
                                }} 
                                className="h-9"
                                required
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Qty (Unit) <span className="text-rose-500">*</span></Label>
                                <Input 
                                  type="number"
                                  min="1"
                                  placeholder="Contoh: 500"
                                  value={item.qty} 
                                  onChange={e => {
                                    const newItems = [...form.items];
                                    newItems[index].qty = e.target.value;
                                    setForm({ ...form, items: newItems });
                                  }} 
                                  className="h-9"
                                  required
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Harga Satuan (Beli) <span className="text-rose-500">*</span></Label>
                                <Input 
                                  type="number"
                                  placeholder="Contoh: 3500"
                                  value={item.hargaBeli} 
                                  onChange={e => {
                                    const newItems = [...form.items];
                                    newItems[index].hargaBeli = e.target.value;
                                    setForm({ ...form, items: newItems });
                                  }} 
                                  className="h-9"
                                  required
                                />
                              </div>
                            </div>
                            
                            <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 flex justify-between items-center mt-2">
                              <span className="text-[10px] font-bold text-slate-500 uppercase">Subtotal Item (Bisa diedit):</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-400">Rp</span>
                                <Input 
                                  type="number"
                                  value={item.amountOverride !== undefined ? item.amountOverride : ((Number(item.qty)||0) * (Number(item.hargaBeli)||0))}
                                  onChange={e => {
                                    const newItems = [...form.items];
                                    newItems[index].amountOverride = e.target.value;
                                    setForm({ ...form, items: newItems });
                                  }}
                                  className="h-8 w-32 text-right font-black text-[#8C6208] border-slate-200"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Total Seluruhnya */}
                  <div className="pt-4 border-t border-slate-100 flex justify-between items-center px-2">
                    <span className="font-bold text-slate-500 uppercase text-xs tracking-wider">Total Transaksi</span>
                    <span className="text-xl font-black text-slate-800">
                      Rp {fmt(form.items.reduce((sum, item) => {
                        const total = item.amountOverride !== undefined && item.amountOverride !== '' 
                                        ? Number(item.amountOverride) 
                                        : (Number(item.qty) || 0) * (Number(item.hargaBeli) || 0);
                        return sum + total;
                      }, 0))}
                    </span>
                  </div>
                  </div>

                  <div className="space-y-2 border border-slate-100 rounded-xl p-3 bg-slate-50/50 mt-4">
                    <div className="flex justify-between items-center mb-1">
                      <Label className="font-bold text-[11px] uppercase tracking-wider text-slate-500">Alokasi Akun COA</Label>
                      <span className="text-[10px] font-medium text-slate-500">
                        Total COA: Rp {fmt((form.coa_allocations || []).reduce((s, c) => s + Number(c.amount || 0), 0))}
                      </span>
                    </div>
                    {(form.coa_allocations || []).map((coa, idx) => (
                      <div key={idx} className="flex gap-2 items-start relative group">
                        <div className="flex-1 grid grid-cols-12 gap-2">
                          <div className="col-span-3">
                            <Select 
                              value={coa.prefix} 
                              onValueChange={val => {
                                const newAlloc = [...form.coa_allocations];
                                newAlloc[idx].prefix = val;
                                newAlloc[idx].account_id = '';
                                newAlloc[idx].account_name = '';
                                setForm({ ...form, coa_allocations: newAlloc });
                              }}
                            >
                              <SelectTrigger className="h-8 text-[11px] bg-white border-slate-200">
                                <SelectValue placeholder="Kategori" />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                <SelectItem value="1">1 - Aset</SelectItem>
                                <SelectItem value="2">2 - Kewajiban</SelectItem>
                                <SelectItem value="3">3 - Ekuitas</SelectItem>
                                <SelectItem value="4">4 - Pendapatan</SelectItem>
                                <SelectItem value="5">5 - HPP</SelectItem>
                                <SelectItem value="6">6 - Beban</SelectItem>
                                <SelectItem value="7">7 - Pdt Lain</SelectItem>
                                <SelectItem value="8">8 - Bbn Lain</SelectItem>
                                <SelectItem value="9">9 - Pajak</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-5">
                            <Select 
                              value={coa.account_id} 
                              onValueChange={val => {
                                const selectedCoa = coaList.find(c => c.id === val);
                                const newAlloc = [...form.coa_allocations];
                                newAlloc[idx].account_id = val;
                                newAlloc[idx].account_name = selectedCoa ? selectedCoa.name : '';
                                setForm({ ...form, coa_allocations: newAlloc });
                              }}
                            >
                              <SelectTrigger className="h-8 text-[11px] bg-white border-slate-200 text-left truncate">
                                <SelectValue placeholder="Pilih Akun" />
                              </SelectTrigger>
                              <SelectContent className="bg-white max-h-48">
                                {coaList.filter(c => !coa.prefix || String(c.id).startsWith(coa.prefix)).map(c => (
                                  <SelectItem key={c.id} value={c.id} className="text-[11px]">
                                    <span className="font-mono text-slate-500 mr-1">{c.id}</span>{c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-4">
                            <Input 
                              type="number"
                              placeholder="Nominal"
                              value={coa.amount}
                              onChange={e => {
                                const newAlloc = [...form.coa_allocations];
                                newAlloc[idx].amount = e.target.value;
                                setForm({ ...form, coa_allocations: newAlloc });
                              }}
                              className="h-8 text-[11px] border-slate-200"
                            />
                          </div>
                        </div>
                        <button 
                          type="button"
                          onClick={() => {
                            const newAlloc = form.coa_allocations.filter((_, i) => i !== idx);
                            setForm({ ...form, coa_allocations: newAlloc });
                          }}
                          className="w-8 h-8 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setForm({ ...form, coa_allocations: [...(form.coa_allocations || []), { prefix: '', account_id: '', amount: '' }] });
                      }}
                      className="w-full h-8 text-[11px] border-dashed text-slate-500 hover:text-[#8C6208] hover:border-[#8C6208] hover:bg-[#F9F5EC] gap-1 rounded-lg mt-2"
                    >
                      <Plus className="w-3 h-3" /> Tambah Akun COA
                    </Button>
                </div>
              </div>

              <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end text-xs">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowFormModal(false)}
                  className="rounded-xl h-9"
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#8C6208] text-white hover:bg-[#724F06] rounded-xl h-9 font-bold"
                >
                  {editItem ? 'Simpan Perubahan' : 'Tambah Transaksi'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {asaConfirmData && (
        <AsaMutasiConfirm
          oldAmount={asaConfirmData.oldAmount}
          newAmount={asaConfirmData.newAmount}
          date={asaConfirmData.date}
          isProcessing={asaConfirmData.isProcessing}
          onConfirm={() => handleAsaConfirm(true)}
          onSkip={() => handleAsaConfirm(false)}
        />
      )}
    </div>
  );
}
